import { flow, set } from 'lodash/fp';
import React from 'react';

import { useFeatureFlags } from '@portals/api/partners';
import { isPartnerFeatureOn } from '@portals/framework';
import {
  CreditCard,
  Gauge,
  RoundDollarWithCursor,
} from '@portals/framework/icons';
import { ProductPricingModel } from '@portals/types';

import { UsageBasedFields } from './UsageBasedFields';
import { DetailsColumnContent } from '../common/DetailsColumnContent';
import { RadioCard } from '../common/RadioCard';
import { RadioCardGroup } from '../common/RadioCardGroup';
import { RadioCardLabel } from '../common/RadioCardLabel';
import { StepId } from '../types';
import { useProductWizard } from '../use-product-wizard';

export function PricingModelStep() {
  const { contextData, setContextData } = useProductWizard();
  const featureFlags = useFeatureFlags();

  return (
    <DetailsColumnContent title="Pricing Model">
      <DetailsColumnContent.Section title="Choose a pricing model">
        <RadioCardGroup
          error={contextData.errors[StepId.PricingModel]?.general}
          value={contextData.product.pricing_model}
          onChange={(value) =>
            setContextData((prev) =>
              flow([
                set(['product', 'pricing_model'], value),
                set(['errors', StepId.PricingModel], ''),
              ])(prev)
            )
          }
        >
          <RadioCard
            value={ProductPricingModel.Standard}
            label={
              <RadioCardLabel
                title="Standard pricing"
                description="Charges customers a pre defined amount for a product or service, offering predictable cost."
                icon={<CreditCard />}
              />
            }
          />

          {isPartnerFeatureOn(featureFlags, 'usage_based_pricing') && (
            <RadioCard
              value={ProductPricingModel.UsageBased}
              label={
                <RadioCardLabel
                  title="Usage Based pricing"
                  description="Charges customers based on usage, offering a cost-effective alternative to fixed pricing."
                  icon={<Gauge />}
                >
                  <UsageBasedFields />
                </RadioCardLabel>
              }
            />
          )}

          {isPartnerFeatureOn(featureFlags, 'personalized_pricing_model') && (
            <RadioCard
              value={ProductPricingModel.Personalized}
              label={
                <RadioCardLabel
                  title="Personalized pricing"
                  description="Your customers will manually enter the price for a product or service."
                  icon={<RoundDollarWithCursor />}
                />
              }
            />
          )}
        </RadioCardGroup>
      </DetailsColumnContent.Section>
    </DetailsColumnContent>
  );
}
